/**
 * Éditeur collaboratif de table brouillon (draft)
 * 
 * Layout en 2 colonnes :
 * - Gauche : configuration de la table + placement des joueurs
 * - Droite : chat + liste des joueurs
 * 
 * Toutes les modifications sont propagées en temps réel via Socket.IO.
 */

import { useCallback, useMemo, useState, useRef, useEffect, type CSSProperties } from 'react';
import type { DraftTable, DraftTableConfig, SeatPosition, CompetRolePreference } from '../types/draft';
import { NB_DONNES_UNLIMITED, getDraftFinalizationError, getActiveSeats } from '../types/draft';
import { computeAutoPlacement, seatsAssignmentsEqual } from '../utils/auto-placement';
import { BACKEND_URL } from '../config';
import { EntryFilteredIcon, EntryOpenIcon, EntryPasswordIcon, StrengthBoostIcon } from './Icons';
import { PlayerAvatar } from './PlayerAvatar';
import { Switch, SwitchRow } from './Switch';
import PlayerSeatList from './PlayerSeatList';
import DraftTableChat from './DraftTableChat';
import PlayerInviteSearch from './PlayerInviteSearch';
import '../styles/spectator.css';

// ============================================
// NbDonnesSelector (composant inline)
// ============================================

const DONNES_STEPS = Array.from({ length: 20 }, (_, i) => (i + 1) * 4); // 4, 8, 12, ..., 80

function isUnlimitedDonnes(value: number): boolean {
  return value === NB_DONNES_UNLIMITED;
}

/** Position horizontale d'un cran (0..maxIndex) alignée sur la zone utile du slider. */
function steppedSliderTickStyle(index: number, maxIndex: number): CSSProperties {
  const fraction = maxIndex > 0 ? index / maxIndex : 0;
  return {
    left: `calc(var(--nb-donnes-thumb-half) + (100% - 2 * var(--nb-donnes-thumb-half)) * ${fraction})`,
  };
}

function NbDonnesSelector({
  value,
  disabled,
  onChange,
  allowUnlimited = false,
}: {
  value: number;
  disabled: boolean;
  onChange: (n: number) => void;
  allowUnlimited?: boolean;
}) {
  const [customMode, setCustomMode] = useState(
    () => !DONNES_STEPS.includes(value) && value !== NB_DONNES_UNLIMITED,
  );
  const customInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (customMode && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [customMode]);

  const sliderLength = allowUnlimited ? DONNES_STEPS.length + 1 : DONNES_STEPS.length;
  const maxSliderIndex = sliderLength - 1;
  const sliderIndex = allowUnlimited
    ? (isUnlimitedDonnes(value) ? 0 : DONNES_STEPS.indexOf(value) + 1)
    : DONNES_STEPS.indexOf(value);
  const currentSliderIndex = sliderIndex >= 0 ? sliderIndex : 0;
  const fillFraction = maxSliderIndex > 0
    ? currentSliderIndex / maxSliderIndex
    : 0;

  const displayValue = allowUnlimited && isUnlimitedDonnes(value)
    ? 'Sans limite'
    : String(value);

  const handleSliderChange = (idx: number) => {
    if (allowUnlimited) {
      onChange(idx === 0 ? NB_DONNES_UNLIMITED : DONNES_STEPS[idx - 1]);
    } else {
      onChange(DONNES_STEPS[idx]);
    }
  };

  return (
    <div className="nb-donnes-selector">
      <div className="nb-donnes-header">
        <span className="nb-donnes-value">{displayValue}</span>
        <label className="nb-donnes-custom-toggle">
          Personnaliser
          <Switch
            compact
            checked={customMode}
            disabled={disabled || (allowUnlimited && isUnlimitedDonnes(value))}
            onChange={(entering) => {
              setCustomMode(entering);
              if (!entering) {
                if (allowUnlimited && isUnlimitedDonnes(value)) return;
                if (!DONNES_STEPS.includes(value)) {
                  const closest = DONNES_STEPS.reduce((prev, curr) =>
                    Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
                  );
                  onChange(closest);
                }
              }
            }}
          />
        </label>
      </div>

      {customMode ? (
        <input
          ref={customInputRef}
          type="number"
          className="draft-config-input draft-config-input-small"
          value={value}
          onChange={(e) => {
            const val = Math.max(1, Math.min(999, parseInt(e.target.value) || 4));
            !disabled && onChange(val);
          }}
          min={1}
          max={999}
          disabled={disabled}
        />
      ) : (
        <div
          className="nb-donnes-slider-wrap"
          style={{ '--fill-fraction': fillFraction } as CSSProperties}
        >
          <div className="nb-donnes-track" />
          <div className="nb-donnes-track-fill" />
          <div className="nb-donnes-thumb" aria-hidden="true" />
          <input
            type="range"
            className="nb-donnes-slider"
            min={0}
            max={maxSliderIndex}
            step={1}
            value={currentSliderIndex}
            onChange={(e) => {
              const idx = parseInt(e.target.value);
              !disabled && handleSliderChange(idx);
            }}
            disabled={disabled}
          />
          <div className="nb-donnes-ticks">
            {allowUnlimited && (
              <div
                className={`nb-donnes-tick major${currentSliderIndex === 0 ? ' active' : ''}${currentSliderIndex >= 0 ? ' filled' : ''}`}
                style={steppedSliderTickStyle(0, maxSliderIndex)}
                onClick={() => !disabled && handleSliderChange(0)}
              >
                <div className="nb-donnes-tick-mark" />
                <span className="nb-donnes-tick-label">∞</span>
              </div>
            )}
            {DONNES_STEPS.map((step, i) => {
              const tickIndex = allowUnlimited ? i + 1 : i;
              const isMajor = step % 20 === 0 || step === 4;
              return (
                <div
                  key={step}
                  className={`nb-donnes-tick${isMajor ? ' major' : ''}${tickIndex === currentSliderIndex ? ' active' : ''}${tickIndex <= currentSliderIndex ? ' filled' : ''}`}
                  style={steppedSliderTickStyle(tickIndex, maxSliderIndex)}
                  onClick={() => !disabled && handleSliderChange(tickIndex)}
                >
                  <div className="nb-donnes-tick-mark" />
                  {isMajor && <span className="nb-donnes-tick-label">{step}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// MinStrengthSlider (composant inline)
// ============================================

const CHAVEY_CRANK_SCORES = [0, 40, 56, 71, 81] as const;
const CHAVEY_CRANK_LABELS = ['Hasard pur', 'Petite', 'Garde', 'Garde Sans', 'Garde Contre'] as const;
const CHAVEY_CRANK_SHORT = ['Hasard', 'Petite', 'Garde', 'G. Sans', 'G. Contre'] as const;
/** Max théorique du barème ; le curseur personnalisé s'arrête à mi-chemin au-delà de Garde Contre. */
const CHAVEY_CUSTOM_ABSOLUTE_MAX = 100;
const CHAVEY_CUSTOM_SLIDER_MAX =
  CHAVEY_CRANK_SCORES[4] + Math.round((CHAVEY_CUSTOM_ABSOLUTE_MAX - CHAVEY_CRANK_SCORES[4]) / 2);

function clampChaveyCustomScore(score: number): number {
  if (score <= 0) return 0;
  return Math.min(CHAVEY_CUSTOM_SLIDER_MAX, Math.round(score));
}

function chaveySeuilToScore(seuil: 0 | 1 | 2 | 3 | 4): number {
  return CHAVEY_CRANK_SCORES[seuil];
}

function chaveyScoreToSeuil(score: number): 0 | 1 | 2 | 3 | 4 {
  if (score <= 0) return 0;
  if (score >= 81) return 4;
  if (score >= 71) return 3;
  if (score >= 56) return 2;
  if (score >= 40) return 1;
  return 1;
}

function isClassicChaveyCrank(score: number): boolean {
  return (CHAVEY_CRANK_SCORES as readonly number[]).includes(score);
}

function chaveyLabelForScore(score: number, custom = false): string {
  const passLabel = custom ? 'Passe' : CHAVEY_CRANK_LABELS[0];
  if (score <= 0) return passLabel;
  if (score >= 81) return CHAVEY_CRANK_LABELS[4];
  if (score >= 71) return CHAVEY_CRANK_LABELS[3];
  if (score >= 56) return CHAVEY_CRANK_LABELS[2];
  if (score >= 40) return CHAVEY_CRANK_LABELS[1];
  return passLabel;
}

function MinStrengthSlider({
  custom,
  seuil,
  minScore,
  disabled,
  onCustomChange,
  onChange,
}: {
  custom: boolean;
  seuil: 0 | 1 | 2 | 3 | 4;
  minScore: number;
  disabled: boolean;
  onCustomChange: (custom: boolean) => void;
  onChange: (payload: {
    forceMinDonnes: boolean;
    forceMinDonnesSeuil: 0 | 1 | 2 | 3 | 4;
    forceMinDonnesCustom?: boolean;
    forceMinDonnesMinScore?: number;
    attaquantTournant?: boolean;
  }) => void;
}) {
  const displayScore = custom ? minScore : chaveySeuilToScore(seuil);
  const boosted = custom && displayScore > 0 && !isClassicChaveyCrank(displayScore);
  const displayLabel = chaveyLabelForScore(displayScore, custom);
  const fillFraction = custom
    ? displayScore / CHAVEY_CUSTOM_SLIDER_MAX
    : seuil / (CHAVEY_CRANK_SCORES.length - 1);

  const handleSliderChange = (raw: number) => {
    if (custom) {
      const score = clampChaveyCustomScore(raw);
      if (score <= 0) {
        onChange({
          forceMinDonnes: false,
          forceMinDonnesSeuil: 0,
          forceMinDonnesCustom: true,
          forceMinDonnesMinScore: 0,
          attaquantTournant: false,
        });
      } else {
        onChange({
          forceMinDonnes: true,
          forceMinDonnesSeuil: chaveyScoreToSeuil(score),
          forceMinDonnesCustom: true,
          forceMinDonnesMinScore: score,
        });
      }
    } else {
      const nextSeuil = raw as 0 | 1 | 2 | 3 | 4;
      if (nextSeuil === 0) {
        onChange({
          forceMinDonnes: false,
          forceMinDonnesSeuil: 0,
          forceMinDonnesCustom: false,
          attaquantTournant: false,
        });
      } else {
        onChange({
          forceMinDonnes: true,
          forceMinDonnesSeuil: nextSeuil,
          forceMinDonnesCustom: false,
        });
      }
    }
  };

  const handleTickClick = (score: number) => {
    if (custom) {
      handleSliderChange(score);
    } else {
      handleSliderChange(chaveyScoreToSeuil(score));
    }
  };

  return (
    <div className="nb-donnes-selector min-strength-selector">
      <div className="nb-donnes-header">
        <span className="nb-donnes-value min-strength-value">
          {displayLabel}
          {boosted && (
            <span
              className="min-strength-boost-wrap"
              title={`${displayScore} points Chavey (entre deux crans)`}
            >
              <StrengthBoostIcon size={14} className="min-strength-boost-icon" />
            </span>
          )}
        </span>
        <label className="nb-donnes-custom-toggle">
          Personnaliser
          <Switch
            compact
            checked={custom}
            disabled={disabled}
            onChange={(next) => !disabled && onCustomChange(next)}
          />
        </label>
      </div>
      <div
        className="nb-donnes-slider-wrap"
        style={{ '--fill-fraction': fillFraction } as CSSProperties}
      >
        <div className="nb-donnes-track" />
        <div className="nb-donnes-track-fill" />
        <div className="nb-donnes-thumb" aria-hidden="true" />
        <input
          type="range"
          className="nb-donnes-slider"
          min={0}
          max={custom ? CHAVEY_CUSTOM_SLIDER_MAX : CHAVEY_CRANK_SCORES.length - 1}
          step={1}
          value={custom ? displayScore : seuil}
          onChange={(e) => !disabled && handleSliderChange(parseInt(e.target.value))}
          disabled={disabled}
        />
        <div className="nb-donnes-ticks">
          {CHAVEY_CRANK_SCORES.map((score, i) => {
            const tickActive = custom ? displayScore === score : seuil === i;
            const tickFilled = custom ? displayScore >= score : seuil >= i;
            return (
              <div
                key={score}
                className={`nb-donnes-tick major${tickActive ? ' active' : ''}${tickFilled ? ' filled' : ''}`}
                style={sliderTickStyleFraction(custom ? score / CHAVEY_CUSTOM_SLIDER_MAX : i / (CHAVEY_CRANK_SCORES.length - 1))}
                onClick={() => !disabled && handleTickClick(score)}
              >
                <div className="nb-donnes-tick-mark" />
                <span className="nb-donnes-tick-label">
                  {custom && i === 0 ? 'Passe' : CHAVEY_CRANK_SHORT[i]}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function sliderTickStyleFraction(fraction: number): CSSProperties {
  return {
    left: `calc(var(--nb-donnes-thumb-half) + (100% - 2 * var(--nb-donnes-thumb-half)) * ${fraction})`,
  };
}

// ============================================
// ConfigTip — info-bulle au survol
// ============================================

function ConfigTip({ text }: { text: string }) {
  return (
    <span
      className="draft-config-tip"
      tabIndex={0}
      onClick={(e) => e.preventDefault()}
      onMouseDown={(e) => e.preventDefault()}
    >
      <span className="draft-config-tip-icon" aria-hidden="true">i</span>
      <span className="draft-config-tip-bubble" role="tooltip">{text}</span>
    </span>
  );
}

// ============================================
// EntryModeSelector (composant inline)
// ============================================

const ENTRY_MODES: Array<{
  value: 1 | 2 | 3;
  Icon: typeof EntryOpenIcon;
  title: string;
  shortTitle: string;
  description: string;
}> = [
  {
    value: 1,
    Icon: EntryOpenIcon,
    title: 'Entrée libre',
    shortTitle: 'Libre',
    description: 'Tout joueur peut rejoindre la table',
  },
  {
    value: 2,
    Icon: EntryFilteredIcon,
    title: 'Entrée filtrée',
    shortTitle: 'Filtrée',
    description: 'Le maître de table approuve chaque joueur',
  },
  {
    value: 3,
    Icon: EntryPasswordIcon,
    title: 'Mot de passe',
    shortTitle: 'Mot de passe',
    description: 'Accès réservé aux joueurs avec le mot de passe',
  },
];

function EntryModeSelector({
  value,
  password,
  disabled,
  onChange,
  onPasswordChange,
}: {
  value: 1 | 2 | 3;
  password: string;
  disabled: boolean;
  onChange: (mode: 1 | 2 | 3) => void;
  onPasswordChange: (pwd: string) => void;
}) {
  return (
    <div className="entry-mode-selector">
      {ENTRY_MODES.map(mode => {
        const ModeIcon = mode.Icon;
        return (
          <button
            key={mode.value}
            type="button"
            className={`entry-mode-card ${value === mode.value ? 'active' : ''}`}
            onClick={() => !disabled && onChange(mode.value)}
            disabled={disabled}
            title={mode.description}
            aria-label={`${mode.title} — ${mode.description}`}
          >
            <span className="entry-mode-icon">
              <ModeIcon size={20} />
            </span>
            <div className="entry-mode-text">
              <span className="entry-mode-title">{mode.title}</span>
              <span className="entry-mode-title-compact">{mode.shortTitle}</span>
              <span className="entry-mode-desc">{mode.description}</span>
            </div>
            <span className={`entry-mode-radio ${value === mode.value ? 'checked' : ''}`} />
          </button>
        );
      })}
      {value === 3 && (
        <div className="entry-mode-password">
          <input
            type="text"
            className="draft-config-input"
            value={password}
            onChange={e => !disabled && onPasswordChange(e.target.value)}
            placeholder="Mot de passe de la table"
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}

// ============================================
// DraftTableEditor
// ============================================

interface DraftTableEditorProps {
  draft: DraftTable;
  currentPseudo: string;
  isAdmin: boolean;
  compet: boolean;
  token: string;
  onUpdateConfig: (config: Partial<DraftTableConfig>) => void;
  onUpdateSeats: (seats: Array<{ playerPseudo: string; seat: SeatPosition; tableIndex?: number }>) => void;
  onInvitePlayer: (pseudo: string) => void;
  onKickPlayer: (pseudo: string) => void;
  onSendMessage: (text: string) => void;
  onSwitchMode: (gameMode: 'libre' | 'defi_libre') => void;
  onFinalize: () => void;
  onClose: () => void;
  playingUsers?: Set<string>;
}

export default function DraftTableEditor({
  draft,
  currentPseudo,
  isAdmin,
  compet,
  token,
  onUpdateConfig,
  onUpdateSeats,
  onInvitePlayer,
  onKickPlayer,
  onSendMessage,
  onSwitchMode,
  onFinalize,
  onClose,
  playingUsers = new Set(),
}: DraftTableEditorProps) {
  const { config, players, status } = draft;

  const joinedCount = players.filter(p => p.status === 'joined').length;
  const pendingCount = players.filter(p => p.status !== 'joined' && p.status !== 'declined').length;
  const isCreating = status === 'creating';
  const isCreated = status === 'created';

  // Panneau d'invitation ouvert / fermé
  const [showInvitePanel, setShowInvitePanel] = useState(false);

  // Placement automatique (activé par défaut)
  const [autoPlacementEnabled, setAutoPlacementEnabled] = useState(true);
  const [rolePreferences, setRolePreferences] = useState<Map<string, CompetRolePreference>>(new Map());
  const lastAutoPlacementRef = useRef<string>('');

  // Pseudos déjà dans le draft
  const draftPseudos = useMemo(
    () => new Set(players.map(p => p.pseudo)),
    [players],
  );

  // Validation pour la finalisation
  const validationError = useMemo(
    () => getDraftFinalizationError(draft),
    [draft],
  );

  // Préférences de rôle (profil) des joueurs connectés
  useEffect(() => {
    const joined = players.filter((p) => p.status === 'joined');
    if (joined.length === 0) {
      setRolePreferences(new Map());
      return;
    }

    let cancelled = false;
    const pseudos = joined.map((p) => p.pseudo).join(',');
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/profile/availability/map?pseudos=${encodeURIComponent(pseudos)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok || cancelled) return;
        const json = await res.json() as {
          competRolePreferences?: Record<string, CompetRolePreference>;
        };
        if (cancelled) return;
        const map = new Map<string, CompetRolePreference>();
        for (const player of joined) {
          const pref = json.competRolePreferences?.[player.pseudo] ?? 'none';
          map.set(player.pseudo.toLowerCase(), pref);
        }
        setRolePreferences(map);
      } catch (err) {
        console.error('[DraftTableEditor] fetch role preferences error:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [players, token]);

  // Réinitialiser le cache quand le toggle est réactivé
  useEffect(() => {
    if (autoPlacementEnabled) {
      lastAutoPlacementRef.current = '';
    }
  }, [autoPlacementEnabled]);

  // Placement auto à chaque nouveau joueur (ou quand le toggle est réactivé)
  useEffect(() => {
    if (!isAdmin || !autoPlacementEnabled || isCreated || isCreating) return;

    const joined = players.filter((p) => p.status === 'joined');
    const dualTable = config.gameMode === 'defi_libre' && config.nbTables === 2;
    const hasUnplaced = joined.some((p) => {
      if (!p.seat) return true;
      if (dualTable && p.tableIndex === undefined) return true;
      return false;
    });
    if (!hasUnplaced) return;

    const placementKey = [
      autoPlacementEnabled,
      joined.map((p) => `${p.pseudo}:${p.status}:${p.seat ?? ''}:${p.tableIndex ?? ''}`).join('|'),
      config.gameMode,
      config.nbTables,
      config.nombreJoueurs,
      config.modeEntree,
      config.tableType,
      [...rolePreferences.entries()].sort().join(','),
    ].join('#');

    if (placementKey === lastAutoPlacementRef.current) return;
    lastAutoPlacementRef.current = placementKey;

    const nextSeats = computeAutoPlacement({
      players,
      config,
      compet: draft.compet,
      createdBy: draft.createdBy,
      rolePreferences,
    });

    const currentSeats = joined
      .filter((p) => p.seat && (!dualTable || p.tableIndex !== undefined))
      .map((p) => ({
        playerPseudo: p.pseudo,
        seat: p.seat!,
        tableIndex: p.tableIndex ?? 0,
      }));

    if (!seatsAssignmentsEqual(nextSeats, currentSeats)) {
      onUpdateSeats(nextSeats);
    }
  }, [
    isAdmin,
    autoPlacementEnabled,
    isCreated,
    isCreating,
    players,
    config,
    draft.compet,
    draft.createdBy,
    rolePreferences,
    onUpdateSeats,
  ]);

  const handleInvite = useCallback((pseudo: string) => {
    onInvitePlayer(pseudo);
  }, [onInvitePlayer]);

  const handleFinalize = useCallback(() => {
    const error = getDraftFinalizationError(draft);
    if (error) {
      window.alert(error);
      return;
    }
    onFinalize();
  }, [draft, onFinalize]);

  return (
    <div className="draft-editor">
      {/* Header */}
      <div className="draft-editor-header">
        <div className="draft-editor-title">
          <h2>
            {config.gameMode === 'defi_libre' ? 'Nouveau défi libre' : 'Nouvelle table libre'}
          </h2>
          <span className="draft-editor-status">
            {isCreating ? 'Création en cours...' :
             isCreated ? 'Table créée !' :
             `${joinedCount} joueur${joinedCount > 1 ? 's' : ''} connecté${joinedCount > 1 ? 's' : ''}`}
          </span>
        </div>
        <div className="draft-editor-actions">
          {!isCreated && (
            <button
              className="draft-finalize-btn"
              onClick={handleFinalize}
              disabled={isCreating || !!validationError || !isAdmin}
              title={validationError || (!isAdmin ? 'Seul un admin peut lancer' : 'Lancer la partie')}
            >
              {isCreating ? 'Création...' : 'Lancer la partie'}
            </button>
          )}
          <button className="draft-close-btn" onClick={onClose} title="Quitter">
            ✕
          </button>
        </div>
      </div>

      {/* Erreur de validation */}
      {validationError && (
        <div className="draft-validation-error">{validationError}</div>
      )}

      {/* Contenu en 2 colonnes */}
      <div className="draft-editor-body">
        {/* Colonne gauche : Configuration */}
        <div className="draft-editor-config">
          <div className="draft-config-section">
            <h3>Configuration</h3>

            {/* Type de table : legacy / neo */}
            <div className="draft-config-row">
              <label>Type de table</label>
              <div className="draft-config-toggle-group">
                <button
                  className={`draft-toggle ${config.tableType === 'legacy' ? 'active' : ''}`}
                  onClick={() => isAdmin && onUpdateConfig({
                    tableType: 'legacy',
                    ...(config.nbDonnes === NB_DONNES_UNLIMITED ? { nbDonnes: 4 } : {}),
                  })}
                  disabled={!isAdmin}
                >
                  Classique
                </button>
                <button
                  className={`draft-toggle ${config.tableType === 'neo' ? 'active' : ''}`}
                  onClick={() => isAdmin && onUpdateConfig({
                    tableType: 'neo',
                    nombreJoueurs: 4,
                    nbDonnes: NB_DONNES_UNLIMITED,
                  })}
                  disabled={!isAdmin}
                  title="Moteur Neo — 4 joueurs uniquement (IA)"
                >
                  Neo (4p)
                </button>
              </div>
            </div>

            {/* Mode : libre / défi libre */}
            {draft.compet && (
              <div className="draft-config-row">
                <label>Mode de jeu</label>
                <div className="draft-config-toggle-group">
                  <button
                    className={`draft-toggle ${config.gameMode === 'libre' ? 'active' : ''}`}
                    onClick={() => isAdmin && onSwitchMode('libre')}
                    disabled={!isAdmin}
                  >
                    Table libre
                  </button>
                  <button
                    className={`draft-toggle ${config.gameMode === 'defi_libre' ? 'active' : ''}`}
                    onClick={() => isAdmin && onSwitchMode('defi_libre')}
                    disabled={!isAdmin}
                  >
                    Défi libre
                  </button>
                </div>
              </div>
            )}

            {/* Nombre de joueurs (legacy : 3/4/5 ; Neo : 4 uniquement) */}
            {!(draft.compet && config.gameMode !== 'defi_libre') && config.gameMode === 'libre' && (
              <div className="draft-config-row">
                <label>Nombre de joueurs</label>
                {config.tableType === 'neo' ? (
                  <span className="draft-config-hint">4 joueurs (moteur Neo — IA à 4 uniquement)</span>
                ) : (
                  <div className="draft-config-toggle-group">
                    {([3, 4, 5] as const).map(n => (
                      <button
                        key={n}
                        className={`draft-toggle ${config.nombreJoueurs === n ? 'active' : ''}`}
                        onClick={() => isAdmin && onUpdateConfig({ nombreJoueurs: n })}
                        disabled={!isAdmin}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Commentaire */}
            <div className="draft-config-row">
              <label>Commentaire</label>
              <input
                type="text"
                className="draft-config-input"
                value={config.commentaire}
                onChange={(e) => isAdmin && onUpdateConfig({ commentaire: e.target.value })}
                placeholder="Description visible par les autres joueurs"
                maxLength={100}
                disabled={!isAdmin}
              />
            </div>

            {/* IA des robots */}
            {config.tableType === 'legacy' && (
              <div className="draft-config-row">
                <label>IA des robots</label>
                {draft.compet ? (
                  <div className="draft-config-toggle-group">
                    <button
                      className={`draft-toggle ${config.ia === 'delphi' ? 'active' : ''}`}
                      onClick={() => isAdmin && onUpdateConfig({ ia: 'delphi' })}
                      disabled={!isAdmin}
                    >
                      Classique
                    </button>
                    <button
                      className={`draft-toggle ${config.ia === 'rv' ? 'active' : ''}`}
                      onClick={() => isAdmin && onUpdateConfig({ ia: 'rv' })}
                      disabled={!isAdmin}
                    >
                      Compétition
                    </button>
                  </div>
                ) : (
                  <span className="draft-config-info">Classique (Delphi)</span>
                )}
              </div>
            )}

            {/* Mode d'entrée */}
            <div className="draft-config-row draft-config-row-block">
              <label>Accès à la table</label>
              <EntryModeSelector
                value={config.modeEntree}
                password={config.motDePasse || ''}
                disabled={!isAdmin}
                onChange={(mode) => onUpdateConfig({ modeEntree: mode })}
                onPasswordChange={(pwd) => onUpdateConfig({ motDePasse: pwd })}
              />
              {config.tableType === 'legacy' && (
                <p className="draft-config-info draft-config-hint">
                  {config.modeEntree === 2
                    ? 'Entrée filtrée : placement libre — chaque joueur rejoint à la place choisie ci-dessous.'
                    : config.modeEntree === 3
                      ? 'Mot de passe + join séquentiel depuis le Sud (comme entrée libre pour les places).'
                      : 'Entrée libre : join séquentiel Sud → Est → … — les humains doivent se suivre sans bot entre eux.'}
                </p>
              )}
            </div>

            {/* Options booléennes */}
            <div className="draft-config-row">
              <SwitchRow
                className="draft-checkbox-label"
                checked={config.petitesAutorisees}
                disabled={!isAdmin}
                onChange={(next) => isAdmin && onUpdateConfig({ petitesAutorisees: next })}
                label="Petites autorisées"
              />
            </div>

            <div className="draft-config-row">
              <SwitchRow
                className="draft-checkbox-label"
                checked={config.iaPeutPrendre}
                disabled={!isAdmin}
                onChange={(next) => isAdmin && onUpdateConfig({ iaPeutPrendre: next })}
                label="IA peut enchérir"
              />
            </div>

            <div className="draft-config-row">
              <SwitchRow
                className="draft-checkbox-label"
                checked={config.tableCachee}
                disabled={!isAdmin}
                onChange={(next) => isAdmin && onUpdateConfig({ tableCachee: next })}
                label="Table cachée"
              />
            </div>

            {/* Options spectateur — Neo uniquement */}
            {config.tableType === 'neo' && (
            <div className="draft-config-section">
              <h3>Spectateurs</h3>
              {draft.compet && (
                <>
                  <div className="draft-config-row">
                    <SwitchRow
                      className="draft-checkbox-label"
                      checked={config.spectateurTempsReel ?? false}
                      disabled={!isAdmin}
                      onChange={(next) => isAdmin && onUpdateConfig({ spectateurTempsReel: next })}
                      label="Spectateurs en temps réel (réservé aux spectateurs placés à la table ci-dessus)"
                    />
                  </div>
                </>
              )}
              {!draft.compet && (
                <div className="draft-config-row">
                  <SwitchRow
                    className="draft-checkbox-label"
                    checked={config.spectateurTempsReel ?? false}
                    disabled={!isAdmin}
                    onChange={(next) => isAdmin && onUpdateConfig({ spectateurTempsReel: next })}
                    label="Spectateurs en temps réel (avertissement aux joueurs)"
                  />
                </div>
              )}
              {!draft.compet && (
                <p className="draft-config-hint">
                  Les joueurs non placés à la table seront spectateurs (donne précédente en différé, ou temps réel
                  si activé ci-dessus).
                </p>
              )}
            </div>
            )}

            {/* Nombre de donnes — commun aux deux modes */}
            <div className="draft-config-row draft-config-row-block">
              <label>Nombre de donnes</label>
              <NbDonnesSelector
                value={config.nbDonnes}
                disabled={!isAdmin}
                allowUnlimited={config.tableType === 'neo'}
                onChange={(n) => onUpdateConfig({ nbDonnes: n })}
              />
            </div>

            {/* Force minimum des donnes + Attaquant tournant — Neo table libre uniquement */}
            {config.tableType === 'neo' && config.gameMode === 'libre' && (
              <>
                <div className="draft-config-row draft-config-row-block">
                  <label className="draft-config-label-with-tip">
                    Force minimum exigée
                    <ConfigTip text="Garantit (barème Chavey) que le joueur attaquant reçoit un jeu au moins aussi fort que le niveau choisi. « Hasard pur » désactive toute contrainte. Cochez « Personnaliser » pour affiner le score minimum entre deux crans classiques (ex. 48 pts entre Petite et Garde). Si le joueur ciblé est un robot, il sera contraint d'enchérir au moins à ce niveau — dans la limite de ce qui reste possible selon les enchères précédentes." />
                  </label>
                  <MinStrengthSlider
                    custom={config.forceMinDonnesCustom ?? false}
                    seuil={config.forceMinDonnes ? config.forceMinDonnesSeuil : 0}
                    minScore={
                      config.forceMinDonnesCustom && config.forceMinDonnesMinScore != null
                        ? clampChaveyCustomScore(config.forceMinDonnesMinScore)
                        : chaveySeuilToScore(config.forceMinDonnes ? config.forceMinDonnesSeuil : 0)
                    }
                    disabled={!isAdmin}
                    onCustomChange={(nextCustom) => {
                      if (!isAdmin) return;
                      if (nextCustom) {
                        const score = clampChaveyCustomScore(
                          chaveySeuilToScore(config.forceMinDonnes ? config.forceMinDonnesSeuil : 0),
                        );
                        onUpdateConfig({
                          forceMinDonnesCustom: true,
                          forceMinDonnesMinScore: score,
                        });
                      } else {
                        const score = config.forceMinDonnesMinScore
                          ?? chaveySeuilToScore(config.forceMinDonnesSeuil);
                        const nextSeuil = chaveyScoreToSeuil(score);
                        onUpdateConfig({
                          forceMinDonnesCustom: false,
                          forceMinDonnesMinScore: undefined,
                          forceMinDonnesSeuil: nextSeuil,
                          forceMinDonnes: nextSeuil > 0,
                        });
                      }
                    }}
                    onChange={(payload) => isAdmin && onUpdateConfig(payload)}
                  />
                </div>

                <div className="draft-config-row">
                  <SwitchRow
                    className="draft-checkbox-label"
                    checked={config.attaquantTournant}
                    disabled={!isAdmin || !config.forceMinDonnes}
                    onChange={(next) => isAdmin && config.forceMinDonnes && onUpdateConfig({ attaquantTournant: next })}
                    label={
                      <span className="draft-checkbox-text-with-tip">
                        Attaquant tournant
                        <ConfigTip text="À chaque nouvelle donne, un joueur différent reçoit le jeu de force minimum. Les places affichent alors uniquement la localisation (Nord, Est…) plutôt que le rôle attaquant/défenseur." />
                      </span>
                    }
                  />
                </div>
              </>
            )}
          </div>

          {/* Paramètres spécifiques au défi libre */}
          {config.gameMode === 'defi_libre' && (
            <div className="draft-config-section">
              <h3>Options du défi</h3>

              <div className="draft-config-row">
                <label>Nombre de tables</label>
                <div className="draft-config-toggle-group">
                  {([1, 2] as const).map(n => (
                    <button
                      key={n}
                      className={`draft-toggle ${config.nbTables === n ? 'active' : ''}`}
                      onClick={() => isAdmin && onUpdateConfig({ nbTables: n })}
                      disabled={!isAdmin}
                    >
                      {n} table{n > 1 ? 's' : ''}
                    </button>
                  ))}
                </div>
              </div>

              <div className="draft-config-row">
                <SwitchRow
                  className="draft-checkbox-label"
                  checked={config.attaquantTournant}
                  disabled={!isAdmin}
                  onChange={(next) => isAdmin && onUpdateConfig({ attaquantTournant: next })}
                  label={
                    <span className="draft-checkbox-text-with-tip">
                      Attaquant tournant
                      <ConfigTip text="Le capitaine d'équipe change à chaque défi. Les places affichent uniquement la localisation (Nord, Est…) plutôt que le rôle attaquant/défenseur." />
                    </span>
                  }
                />
              </div>

              <div className="draft-config-row">
                <SwitchRow
                  className="draft-checkbox-label"
                  checked={config.surcontratAutorise}
                  disabled={!isAdmin}
                  onChange={(next) => isAdmin && onUpdateConfig({ surcontratAutorise: next })}
                  label="Surcontrat autorisé"
                />
              </div>

              <div className="draft-config-row">
                <SwitchRow
                  className="draft-checkbox-label"
                  checked={config.defiOuvert}
                  disabled={!isAdmin}
                  onChange={(next) => isAdmin && onUpdateConfig({ defiOuvert: next })}
                  label="Défi ouvert"
                />
              </div>
            </div>
          )}

          {/* Placement des joueurs */}
          <div className="draft-config-section">
            <h3>Placement des joueurs</h3>
            {config.tableType === 'legacy' && config.gameMode === 'libre' && (
              <p className="draft-config-info draft-config-hint">
                {config.modeEntree === 2
                  ? 'Entrée filtrée : placement libre — un humain en Sud (maître de table) est requis ; les bots peuvent occuper les autres places.'
                  : 'Entrée libre ou mot de passe : les humains doivent se suivre depuis le Sud, sans bot intercalé.'}
              </p>
            )}
            <PlayerSeatList
              nombreJoueurs={config.gameMode === 'defi_libre' ? 4 : config.nombreJoueurs}
              compet={draft.compet}
              players={players}
              isAdmin={isAdmin}
              nbTables={config.gameMode === 'defi_libre' ? config.nbTables : 1}
              hideRoleLabels={
                draft.compet && (
                  (config.tableType === 'neo'
                    && config.gameMode === 'libre'
                    && (!config.forceMinDonnes || config.attaquantTournant))
                  || (config.gameMode === 'defi_libre' && config.attaquantTournant)
                )
              }
              masterSeat={
                config.tableType === 'legacy' && config.gameMode === 'libre'
                  ? getActiveSeats(config.nombreJoueurs, draft.compet)[0]
                  : undefined
              }
              onUpdateSeats={onUpdateSeats}
              onKickPlayer={onKickPlayer}
              autoPlacementEnabled={autoPlacementEnabled}
              onAutoPlacementChange={setAutoPlacementEnabled}
            />
          </div>
        </div>

        {/* Colonne droite : Joueurs + Chat */}
        <div className="draft-editor-sidebar">
          {/* Liste des joueurs */}
          <div className="draft-players-section">
            <h3>
              Joueurs ({joinedCount}{pendingCount > 0 ? ` · ${pendingCount} en attente` : ''})
              {isAdmin && (
                <button
                  className="draft-invite-btn"
                  onClick={() => setShowInvitePanel(prev => !prev)}
                  title="Inviter un joueur"
                >
                  {showInvitePanel ? '- Fermer' : '+ Inviter'}
                </button>
              )}
            </h3>

            {/* Panneau d'invitation autocomplete */}
            {showInvitePanel && isAdmin && (
              <PlayerInviteSearch
                playingUsers={playingUsers}
                draftPseudos={draftPseudos}
                currentPseudo={currentPseudo}
                compet={compet}
                token={token}
                onInvite={handleInvite}
                onClose={() => setShowInvitePanel(false)}
              />
            )}

            <div className="draft-players-list">
              {players.map(player => (
                <div key={player.pseudo} className={`draft-player-item status-${player.status}`}>
                  <PlayerAvatar pseudo={player.pseudo} size={26} />
                  <span className="draft-player-name">{player.pseudoDisplay}</span>
                  <div className="draft-player-badges">
                    {player.isAdmin && <span className="draft-badge admin" title="Admin">A</span>}
                    {player.status === 'invited' && <span className="draft-badge invited">Invité</span>}
                    {player.status === 'declined' && <span className="draft-badge declined">A refusé</span>}
                    {player.status === 'waiting_game' && <span className="draft-badge waiting">En jeu</span>}
                    {player.status === 'waiting_connect' && <span className="draft-badge offline">Hors ligne</span>}
                  </div>
                  {isAdmin && player.pseudo.toLowerCase() !== currentPseudo.toLowerCase() && player.status === 'joined' && (
                    <button
                      className="draft-kick-btn"
                      onClick={() => onKickPlayer(player.pseudo)}
                      title="Retirer ce joueur"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Chat */}
          <DraftTableChat
            messages={draft.messages}
            currentPseudo={currentPseudo}
            onSendMessage={onSendMessage}
          />
        </div>
      </div>
    </div>
  );
}
